import {
  ActionIcon,
  Box,
  Flex,
  HoverCard,
  Pagination,
  ScrollArea,
  Skeleton,
  Stack,
  Table,
  Text,
  Tooltip,
  UnstyledButton,
  Badge,
  Autocomplete,
  Modal,
  LoadingOverlay,
} from '@mantine/core';
import { IconBoxOff, IconRefresh, IconX } from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import React, { useEffect, useState } from 'react';
import COLORS from '../../../constants/colors';
import {
  handleErrorResponse,
  isArrayAndHasContent,
  scanActionBadgeColorMapper,
  userWiseAutocompleteLocationDataWithId,
} from '../../../utils/utils';
import NoDataPlaceholder from '../../Global/NoDataPlaceholder';
import SearchInput from '../../Global/SearchInput';
import {
  fetchAllBlockedCbcs,
  fetchBlockedCbcs,
} from '../../../services/orders';
import dayjs from 'dayjs';
import { useSelector } from 'react-redux';
import CBCHistory from '../Modal/CBCHistory';

const BlockedCbcTab = ({ locationData, currentTab, requiredTab }) => {
  const appUser = useSelector((state) => state.auth.user);
  const [page, setPage] = useState(1);
  const [searchKey, setSearchKey] = useState(null);
  const [searchByLocation, setSearchByLocation] = useState(null);
  const [locationSearchKey, setLocationSearchKey] = useState(null);
  const [invokingRefreshForSearchInput, setInvokingRefreshForSearchInput] =
    useState(null);

  const [showCbcHistoryModal, setShowCbcHistoryModal] = useState(false);
  const [selectedCbc, setSelectedCbc] = useState(null);

  const { data, isLoading, isFetching, refetch, error } = useQuery({
    queryKey: [
      'blockedcbcs',
      page,
      null,
      null,
      null,
      searchByLocation,
      null,
      null,
      searchKey,
    ],
    queryFn: fetchAllBlockedCbcs,
    refetchOnWindowFocus: true,
    keepPreviousData: true,
    // refetchInterval: 5 * 60 * 1000,
    // refetchIntervalInBackground: true,
    enabled: currentTab == requiredTab ? true : false,
    retry: false,
    onError: (error) => {
      handleErrorResponse(error);
    },
  });

  useEffect(() => {
    if (data) {
      refetch();
    }
  }, []);

  const handleSearch = (value) => {
    setPage(1);
    setSearchKey(value.trim());
  };

  const handleRefresh = () => {
    setPage(1);
    setInvokingRefreshForSearchInput(!invokingRefreshForSearchInput);
    setSearchKey('');
    setLocationSearchKey(null);
    setSearchByLocation(null);
  };

  if (isLoading)
    return (
      <div>
        <Skeleton height="80vh" mt={10} />
      </div>
    );

  if (error)
    return (
      <div>
        <NoDataPlaceholder apiError={true} />
      </div>
    );

  const { cbcs, total } = data.data.data;

  return (
    <div>
      <Modal
        opened={showCbcHistoryModal}
        onClose={() => {
          setShowCbcHistoryModal(false);
          setSelectedCbc(null);
        }}
        title="CBC Details"
        size="xxl"
        centered={true}>
        <CBCHistory
          uid={selectedCbc?.uid}
          selectedCBC={selectedCbc}
          openCBC={false}
        />
      </Modal>
      <Flex justify="space-between" align="center" py="sm">
        <Flex gap={20}>
          <div
            style={{
              minWidth: '300px',
            }}>
            <SearchInput
              handleRefresh={handleRefresh}
              handleSearch={handleSearch}
              placeholder="Search Barcode, Packcode, Name"
              invokeRefresh={invokingRefreshForSearchInput}
              refreshBtn={false}
            />
          </div>
          <Autocomplete
            placeholder="Search by Point"
            sx={{
              minWidth: '250px',
            }}
            onChange={(value) => {
              if (locationSearchKey && locationSearchKey.length === 0) {
                setLocationSearchKey(null);
              } else {
                setLocationSearchKey(value);
              }
            }}
            limit={locationData?.length || 0}
            maxDropdownHeight="200px"
            dropdownPosition="bottom"
            nothingFound="No options"
            value={locationSearchKey || ''}
            onItemSubmit={(e) => {
              setSearchByLocation(e.id);
            }}
            rightSection={
              searchByLocation ? (
                <UnstyledButton
                  sx={{
                    justifyContent: 'center',
                    alignItems: 'center',
                    display: 'flex',
                  }}
                  onClick={() => {
                    setLocationSearchKey(null);
                    setSearchByLocation(null);
                  }}>
                  <IconX size={15} />
                </UnstyledButton>
              ) : null
            }
            data={
              userWiseAutocompleteLocationDataWithId({
                appUser: appUser,
                locationData: locationData,
              }) || []
            }
          />

          <Flex gap={20} align="center" justify="center">
            <Tooltip label="Refresh">
              <ActionIcon
                size="lg"
                onClick={() => {
                  handleRefresh();
                }}
                sx={{
                  backgroundColor: COLORS.orange,
                }}
                variant="filled">
                <IconRefresh size={18} />
              </ActionIcon>
            </Tooltip>
          </Flex>
        </Flex>
      </Flex>
      <Stack
        sx={{
          minHeight: '50vh',
        }}>
        {isArrayAndHasContent(cbcs) ? (
          <ScrollArea h="65vh">
            <Table highlightOnHover>
              <thead
                style={{
                  position: 'sticky',
                  top: 0,
                  zIndex: 1,
                  backgroundColor: 'white',
                }}>
                <tr>
                  <th>Barcode</th>
                  <th>Packcode</th>
                  <th>SKU</th>
                  <th>Name</th>
                  <th>Location</th>
                  <th>Quantity - TH (sticks)</th>
                  <th>Production Date</th>
                  <th>Expiry Date</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                <LoadingOverlay visible={isFetching} />
                {cbcs.map((cbc, i) => (
                  <tr key={i}>
                    <td>
                      <Box>
                        <UnstyledButton
                          onClick={() => {
                            setShowCbcHistoryModal(true);
                            setSelectedCbc({
                              ...cbc,
                              productName: cbc?.productName,
                              loationName: cbc?.locationName,
                            });
                          }}>
                          <Text color="blue">{cbc.barcode}</Text>
                        </UnstyledButton>
                      </Box>
                    </td>
                    <td>
                      <Box>
                        <Text>{cbc.packcode}</Text>
                      </Box>
                    </td>

                    <td>
                      <Box>
                        <Text>{cbc.sku}</Text>
                      </Box>
                    </td>
                    <td
                      style={{
                        maxWidth: '200px',
                      }}>
                      <Box>
                        <Text>
                          {cbc.shipmentFormatName
                            ? cbc.shipmentFormatName
                            : cbc.productName}
                        </Text>
                      </Box>
                    </td>

                    <td>
                      <HoverCard width={280} shadow="md">
                        <HoverCard.Target>
                          <Text fz="sm">{cbc.locationName}</Text>
                        </HoverCard.Target>
                        <HoverCard.Dropdown>
                          <Text size="md">{cbc.locationName}</Text>
                          <Text size="sm">
                            {cbc.district || 'N/A'}, {cbc.division || 'N/A'}
                          </Text>
                          <Text size="sm">{cbc.address}</Text>
                        </HoverCard.Dropdown>
                      </HoverCard>
                    </td>
                    <td>
                      <Box>
                        <Text>{cbc.stickCount / 1000}</Text>
                      </Box>
                    </td>
                    <td>
                      <Box>
                        <Text>{cbc.productionDate} </Text>
                      </Box>
                    </td>
                    <td>
                      <Box>
                        <Text>{cbc.expiryDate}</Text>
                      </Box>
                    </td>

                    <td>
                      <Badge color={scanActionBadgeColorMapper(cbc?.cbcStatus)}>
                        {cbc?.cbcStatus}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </ScrollArea>
        ) : (
          <Flex align="center" justify="center" style={{ height: '68vh' }}>
            <NoDataPlaceholder
              title="No Blocked CBCs found"
              subtext={' '}
              icon={<IconBoxOff size={70} color="#4562f7" />}
            />
          </Flex>
        )}
        <Flex justify="end">
          <Pagination
            mt="20px"
            value={page}
            onChange={setPage}
            total={Math.ceil(total / 10)}
          />
        </Flex>
      </Stack>
    </div>
  );
};

export default BlockedCbcTab;
